/**
 * Vue Router Configuration
 *
 * Defines all application routes with type safety and authentication guards
 */

import { createRouter, createWebHistory, RouteRecordRaw, NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import store from '@/store';

// Static imports (needed immediately / small pages)
import UserAuth from '../pages/auth/UserAuth.vue';
import User from '../pages/user/UserPage.vue';
import NotFound from '../pages/NotFound.vue';

// Lazy-loaded imports (behind auth guard, loaded on demand)
const Dashboard = () => import('../pages/user/DashboardPage.vue');
const AdvancedDashboard = () => import('../pages/user/AdvancedDashboardPage.vue');
const Alarms = () => import('../pages/user/AlarmsPage.vue');
const Devices = () => import('../pages/user/DevicesPage.vue');
const Settings = () => import('../pages/user/SettingsPage.vue');

/**
 * Route configuration with type safety
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/dashboard'
  },

  {
    path: '/auth',
    name: 'auth',
    component: UserAuth,
    meta: { requiresUnauth: true }
  },
  {
    path: '/user',
    name: 'user',
    component: User,
    meta: { requiresAuth: true }
  },
  {
    path: '/:notFound(.*)',
    name: 'notfound',
    component: NotFound
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: Dashboard,
    meta: { requiresAuth: true }
  },
  {
    path: '/advanced-dashboard',
    name: 'advanced-dashboard',
    component: AdvancedDashboard,
    meta: { requiresAuth: true }
  },
  {
    path: '/alarms',
    name: 'alarms',
    component: Alarms,
    meta: { requiresAuth: true }
  },
  {
    path: '/devices',
    name: 'devices',
    component: Devices,
    meta: { requiresAuth: true }
  },
  {
    path: '/settings',
    name: 'settings',
    component: Settings,
    meta: { requiresAuth: true }
  },
];

/**
 * Create router instance with typed configuration
 */
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

/**
 * Global authentication guard
 * Enforces authentication requirements and redirects based on auth state
 */
router.beforeEach((
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): void => {
  // Check authentication state from Vuex store
  const isAuthenticated = store.getters && (store.getters['auth/isAuthenticated'] || store.getters['auth/token']);

  console.log('[Router Guard] Navigation:', from.path, '→', to.path);
  console.log('[Router Guard] isAuthenticated:', isAuthenticated);
  console.log('[Router Guard] token:', store.getters['auth/token']);

  // Redirect root or home based on auth state
  if (to.path === '/' || to.path === '/home') {
    if (isAuthenticated) {
      console.log('[Router Guard] Redirecting authenticated user to /dashboard');
      return next('/dashboard');
    } else {
      console.log('[Router Guard] Redirecting unauthenticated user to /auth');
      return next('/auth');
    }
  }

  // Enforce route meta guards - protected routes
  if (to.meta && to.meta.requiresAuth && !isAuthenticated) {
    console.log('[Router Guard] Protected route, redirecting to /auth');
    return next('/auth');
  }

  // Enforce route meta guards - guest-only routes
  // Allow navigation to /auth if coming from a protected route (logout scenario)
  if (to.meta && to.meta.requiresUnauth && isAuthenticated) {
    // Allow logout: if coming from a protected route, let them go to /auth
    const fromProtectedRoute = from.meta && from.meta.requiresAuth;
    if (fromProtectedRoute && to.path === '/auth') {
      console.log('[Router Guard] Allowing logout navigation from protected route to /auth');
      return next();
    }
    
    console.log('[Router Guard] Guest-only route, redirecting authenticated user to /dashboard');
    return next('/dashboard');
  }

  console.log('[Router Guard] Allowing navigation');
  return next();
});

export default router;
